import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
  Radio,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  DialogContentText,
} from '@mui/material';
import { getCompanyNameFromDOT, getCompanyNameFromMC, signupUser, checkEmailExists, signinUser } from '../api/auth';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import 'react-phone-number-input/style.css';
import TermsAndConditions from './TermsAndConditions';
import PrivacyPolicy from './PrivacyPolicy';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import GroupsIcon from '@mui/icons-material/Groups';
import GoogleSignInButton from './GoogleSignInButton';
import axios from 'axios';
import { auth, sendPasswordResetEmail } from '../utils/firebaseClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const SUBSCRIPTION_STATUSES = {
  VALID: ['on_trial', 'trialing', 'active', 'paid'],
  NEEDS_SUBSCRIPTION: ['carrier']
};

const FIELD_LABELS = {
  email: 'Email',
  password: 'Password',
  confirm_password: 'Confirm Password',
  phone_number: 'Phone number',
  company_name: 'Company Name',
  street: 'Street Address',
  cityStZip: 'City, State, Zip',
  employees: 'Number of Employees',
  dot_number: 'DOT Number',
  mc_number: 'MC Number',
  power_units: 'Number of Power Units',
};

const PASSWORD_REGEX = {
  minLength: 8,
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  number: /[0-9]/,
  specialChar: /[!@#$%^&*()]/,
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const US_PHONE_MIN_LENGTH = 10;
const US_PHONE_MAX_LENGTH = 10;

const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input.replace(/[<>]/g, '');
};

const sanitizeFormData = (formData) => {
  const sanitized = {};
  Object.keys(formData).forEach(key => {
    const value = formData[key];
    sanitized[key] = typeof value === 'string' ? value.trim().replace(/[<>]/g, '') : value;
  });
  return sanitized;
};

const validatePassword = (password) => {
  if (password.length < PASSWORD_REGEX.minLength) {
    return 'Password must be at least 8 characters long.';
  }
  if (!PASSWORD_REGEX.uppercase.test(password)) {
    return 'Password must contain at least one uppercase letter.';
  }
  if (!PASSWORD_REGEX.lowercase.test(password)) {
    return 'Password must contain at least one lowercase letter.';
  }
  if (!PASSWORD_REGEX.number.test(password)) {
    return 'Password must contain at least one number.';
  }
  if (!PASSWORD_REGEX.specialChar.test(password)) {
    return 'Password must contain at least one special character (!@#$%^&*()).';
  }
  return '';
};

const validateEmail = (email) => EMAIL_REGEX.test(email);

const validateCityStateZip = (value) => {
  if (!value || value.trim().length < 5) {
    return 'City, State, Zip is required';
  }
  return '';
};

const validateDOTNumber = (dotNumber) => {
  const cleaned = dotNumber.replace(/\D/g, '');
  if (cleaned.length < 6 || cleaned.length > 8) {
    return 'DOT number must be 6-8 digits';
  }
  return '';
};

const validateMCNumber = (mcNumber) => {
  const cleaned = mcNumber.replace(/[^0-9]/g, '');
  if (cleaned.length < 6) {
    return 'MC number must be at least 6 digits';
  }
  return '';
};

const validateCompanyName = (name) => {
  if (!name || name.trim().length < 2) {
    return 'Company name must be at least 2 characters';
  }
  if (name.length > 200) {
    return 'Company name is too long';
  }
  return '';
};

// Helper: clears all company-related fields fetched from a lookup
const CLEARED_COMPANY_FIELDS = {
  legal_name: '',
  dba_name: '',
  street: '',
  cityStZip: '',
  power_units: '',
  company_name: '',
  mc_number: '',
};

export default function AuthForm({ isSignUp, role, onToggle, onSignInSuccess, onSignInError, onRoleError }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [dotDialog, setDotDialog] = useState({ open: false, status: 'idle' });
  
  const [formState, setFormState] = useState({
    email: '',
    password: '',
    confirm_password: '',
    company_name: '',
    legal_name: '',
    dba_name: '',
    street: '', 
    cityStZip: '', 
    phone_number: '',
    employees: '',
    dot_number: '',
    mc_number: '',
    power_units: '',
    fmcsa_email: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [errors, setErrors] = useState({});
  const [fieldStatus, setFieldStatus] = useState({});
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [isAutofilled, setIsAutofilled] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [sessionConflictOpen, setSessionConflictOpen] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [loginErrorDialogOpen, setLoginErrorDialogOpen] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState('');

  const [planPrice, setPlanPrice] = useState(null);
  const [trialDays, setTrialDays] = useState(null);
  const [isPricingLoading, setIsPricingLoading] = useState(true);

  useEffect(() => {
    // Pre-fill fields if user is redirected from a new Google sign-in
    if (location.state?.prefill && isSignUp) {
      setFormState(prev => ({
        ...prev,
        email: location.state.prefill.email || prev.email,
        company_name: location.state.prefill.company_name || prev.company_name,
        legal_name: location.state.prefill.company_name || prev.legal_name,
      }));
      // Clear the history state so pre-fill doesn't persist on manual refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, isSignUp]);

  useEffect(() => {
    const fetchPricingDetails = async () => {
      setIsPricingLoading(true);
      setPlanPrice(null);
      try {
        const priceIdMap = {
          shipper: process.env.REACT_APP_STRIPE_PRICE_ID_SHIPPER,
          carrier: process.env.REACT_APP_STRIPE_PRICE_ID_CARRIER,
          broker: process.env.REACT_APP_STRIPE_PRICE_ID_BROKER,
        };
        const selectedPriceId = priceIdMap[role];
        if (!selectedPriceId) {
          setIsPricingLoading(false);
          return;
        }

        const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
        const token = localStorage.getItem('jwtToken');

        const response = await axios.post(`${API_BASE}/api/stripe/create-subscription`, 
          { priceId: selectedPriceId, previewOnly: true },
          { 
            headers: { 
              ...(token && { Authorization: `Bearer ${token}` }) 
            } 
          }
        );

        if (response.data.amount !== undefined) setPlanPrice(response.data.amount);
        if (response.data.trialDays !== undefined) setTrialDays(response.data.trialDays);

      } catch (err) {
        console.error('Pricing fetch error:', err);
      } finally {
        setIsPricingLoading(false);
      }
    };
    fetchPricingDetails();
  }, [role]);

  useEffect(() => {
    // Guard: Don't clear state if we just pre-filled from Google
    if (location.state?.prefill) return;

    setFormState({
      email: '',
      password: '',
      confirm_password: '',
      company_name: '',
      legal_name: '',
      dba_name: '',
      street: '',
      cityStZip: '',
      phone_number: '',
      employees: '',
      dot_number: '',
      mc_number: '',
      power_units: '',
    });
    setErrors({});
    setFieldStatus({});
    setAgreementChecked(false);
  }, [isSignUp, role]);

  useEffect(() => {
    if (loginAttempts >= 5) {
      setIsRateLimited(true);
      const timer = setTimeout(() => {
        setLoginAttempts(0);
        setIsRateLimited(false);
      }, 300000); 
      return () => clearTimeout(timer);
    }
  }, [loginAttempts]);

  useEffect(() => {
    if (formState.phone_number && isSignUp) {
      const rawDigits = formState.phone_number.replace(/[^0-9]/g, '');
      if (rawDigits && rawDigits.length === US_PHONE_MIN_LENGTH) {
        const areaCode = rawDigits.slice(0, 3);
        const prefix = rawDigits.slice(3, 6);
        
        if (areaCode[0] === '0' || areaCode[0] === '1' || prefix[0] === '0' || prefix[0] === '1') {
          setIsPhoneValid(false);
          setErrors(prev => ({ ...prev, phone_number: 'Invalid US phone number format.' }));
          setFieldStatus(prev => ({ ...prev, phone_number: 'error' }));
        } else {
          setIsPhoneValid(true);
          setErrors(prev => ({ ...prev, phone_number: '' }));
          setFieldStatus(prev => ({ ...prev, phone_number: 'success' }));
        }
      }
    }
  }, [formState.phone_number, isSignUp]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = sanitizeInput(value);
    let fieldError = '';

    if (name === 'employees' || name === 'power_units') {
      if (/[a-zA-Z]/.test(value)) {
        fieldError = 'This field can only contain numbers.';
        setErrors(prev => ({ ...prev, [name]: fieldError }));
        return; 
      }
      newValue = value.replace(/[^0-9]/g, '');
    }
  
    setFormState(prev => ({ ...prev, [name]: newValue }));

    if (name === 'email') {
      if (validateEmail(newValue)) {
        setErrors(prev => ({ ...prev, email: '' }));
        setFieldStatus(prev => ({ ...prev, email: 'success' }));
      } else {
        setErrors(prev => ({ ...prev, email: 'Invalid email format' }));
        setFieldStatus(prev => ({ ...prev, email: 'error' }));
      }
    } else if (name === 'fmcsa_email') {
      if (validateEmail(newValue)) {
        setErrors(prev => ({ ...prev, fmcsa_email: '' }));
        setFieldStatus(prev => ({ ...prev, fmcsa_email: 'success' }));
      } else {
        setErrors(prev => ({ ...prev, fmcsa_email: 'Invalid email format' }));
        setFieldStatus(prev => ({ ...prev, fmcsa_email: 'error' }));
      }
    } else if (name === 'password' && isSignUp) {
      const passwordError = validatePassword(newValue);
      if (passwordError) {
        setErrors(prev => ({ ...prev, password: passwordError }));
        setFieldStatus(prev => ({ ...prev, password: 'error' }));
      } else {
        setErrors(prev => ({ ...prev, password: '' }));
        setFieldStatus(prev => ({ ...prev, password: 'success' }));
      }
    } else if (name === 'confirm_password') {
      if (newValue !== formState.password) {
        setErrors(prev => ({ ...prev, confirm_password: 'Passwords do not match.' }));
        setFieldStatus(prev => ({ ...prev, confirm_password: 'error' }));
      } else {
        setErrors(prev => ({ ...prev, confirm_password: '' }));
        setFieldStatus(prev => ({ ...prev, confirm_password: 'success' }));
      }
    } else if (name === 'cityStZip' && isSignUp) {
      const cityError = validateCityStateZip(newValue);
      if (cityError) {
        setErrors(prev => ({ ...prev, cityStZip: cityError }));
        setFieldStatus(prev => ({ ...prev, cityStZip: 'error' }));
      } else {
        setErrors(prev => ({ ...prev, cityStZip: '' }));
        setFieldStatus(prev => ({ ...prev, cityStZip: 'success' }));
      }
    } else if (name === 'company_name' || name === 'legal_name') {
      const companyError = validateCompanyName(newValue);
      if (companyError) {
        setErrors(prev => ({ ...prev, [name]: companyError }));
        setFieldStatus(prev => ({ ...prev, [name]: 'error' }));
      } else {
        setErrors(prev => ({ ...prev, [name]: '' }));
        setFieldStatus(prev => ({ ...prev, [name]: 'success' }));
      }
    }
  };

  const handlePhoneNumberChange = (e) => {
    const rawDigits = e.target.value.replace(/[^0-9]/g, '');

    if (rawDigits.length > US_PHONE_MAX_LENGTH) {
      return;
    }

    let displayValue = rawDigits;
    if (rawDigits.length >= 1) {
      if (rawDigits.length <= 3) {
        displayValue = rawDigits;
      } else if (rawDigits.length <= 6) {
        displayValue = `(${rawDigits.slice(0, 3)}) ${rawDigits.slice(3)}`;
      } else {
        displayValue = `(${rawDigits.slice(0, 3)}) ${rawDigits.slice(3, 6)}-${rawDigits.slice(6, 10)}`;
      }
    }

    setFormState(prev => ({ ...prev, phone_number: displayValue }));

    if (!rawDigits) {
      setErrors(prev => ({ ...prev, phone_number: 'Phone Number is required.' }));
      setFieldStatus(prev => ({ ...prev, phone_number: 'error' }));
      setIsPhoneValid(false);
      return;
    }

    if (rawDigits.length < US_PHONE_MIN_LENGTH) {
      setIsPhoneValid(false);
      setErrors(prev => ({ ...prev, phone_number: `Phone number must be ${US_PHONE_MIN_LENGTH} digits.` }));
      setFieldStatus(prev => ({ ...prev, phone_number: 'error' }));
    } else if (rawDigits.length === US_PHONE_MIN_LENGTH) {
      const areaCode = rawDigits.slice(0, 3);
      const prefix = rawDigits.slice(3, 6);
    
      if (areaCode[0] === '0' || areaCode[0] === '1') {
        setIsPhoneValid(false);
        setErrors(prev => ({ ...prev, phone_number: 'Area code cannot start with 0 or 1.' }));
        setFieldStatus(prev => ({ ...prev, phone_number: 'error' }));
      } else if (prefix[0] === '0' || prefix[0] === '1') {
        setIsPhoneValid(false);
        setErrors(prev => ({ ...prev, phone_number: 'Exchange code cannot start with 0 or 1.' }));
        setFieldStatus(prev => ({ ...prev, phone_number: 'error' }));
      } else {
        setIsPhoneValid(true);
        setErrors(prev => ({ ...prev, phone_number: '' }));
        setFieldStatus(prev => ({ ...prev, phone_number: 'success' }));
      }
    }
  };

  const handleEmailBlur = async () => {
    if (isSignUp && formState.email && validateEmail(formState.email)) {
      setIsCheckingEmail(true);
      try {
        const { exists } = await checkEmailExists(formState.email, role);
        if (exists) {
          setErrors(prev => ({ ...prev, email: 'A user with this email already exists.' }));
          setFieldStatus(prev => ({ ...prev, email: 'error' }));
        } else {
          setErrors(prev => ({ ...prev, email: '' }));
          setFieldStatus(prev => ({ ...prev, email: 'success' }));
        }
      } catch (error) {
        console.error('Error checking email:', error);
      } finally {
        setIsCheckingEmail(false);
      }
    }
  };

  const handleDotChange = async (e) => {
    const { value } = e.target;
    const displayValue = value.replace(/\D/g, '');

    setFormState(prev => ({
      ...prev,
      dot_number: displayValue,
      ...CLEARED_COMPANY_FIELDS,
    }));
    setIsAutofilled(false);
    setFieldStatus(prev => ({ ...prev, dot_number: '' }));
    setErrors(prev => ({ ...prev, dot_number: '' }));

    const validationError = validateDOTNumber(displayValue);

    if (validationError) {
      setErrors(prev => ({ ...prev, dot_number: validationError }));
      return;
    }

    if (displayValue.length >= 6) {
      // Open the dialog in loading mode
      setDotDialog({ open: true, status: 'loading' });
      try {
        const data = await getCompanyNameFromDOT(displayValue);

        if (data) {
          setFormState(prev => ({
            ...prev,
            legal_name: sanitizeInput(data.legalName) || '',
            company_name: sanitizeInput(data.legalName) || '',
            dba_name: sanitizeInput(data.dbaName) || '',
            street: sanitizeInput(data.street) || '',
            cityStZip: sanitizeInput(data.cityStZip) || '',
            power_units: data.powerUnits || prev.power_units,
            mc_number: (data.mcNumber || data.mc_number) ? `MC-${(data.mcNumber || data.mc_number).toString().replace(/^MC-?/i, '')}` : '',
          }));
          setIsAutofilled(true);
          setFieldStatus(prev => ({ ...prev, dot_number: 'success' }));
          // Close dialog on success
          setDotDialog({ open: false, status: 'idle' });
        }
      } catch (error) {
        console.error("Lookup error", error);
        setFieldStatus(prev => ({ ...prev, dot_number: 'error' }));
        // Change dialog to error mode
        setDotDialog({ open: true, status: 'error' });
      }
    }
  };

  const handleMcChange = (e) => {
    let val = e.target.value.toUpperCase();
    if (!val.startsWith('MC-')) val = 'MC-' + val.replace(/^MC-?/, '');
    const digits = val.split('MC-')[1] || '';
    setFormState(prev => ({ ...prev, mc_number: `MC-${digits.replace(/\D/g, '')}` }));
  };

  const validateForm = () => {
    const newErrors = {};
 
    if (!formState.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formState.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (!formState.password) {
      newErrors.password = 'Password is required';
    }
    
    if (isSignUp) {
      const passwordError = validatePassword(formState.password);
      if (passwordError) newErrors.password = passwordError;

      if (!formState.phone_number) {
        newErrors.phone_number = 'Phone Number is required';
      } else {
        const rawDigits = formState.phone_number.replace(/[^0-9]/g, '');
        
        if (rawDigits.length !== US_PHONE_MIN_LENGTH) {
          newErrors.phone_number = `Phone number must be exactly ${US_PHONE_MIN_LENGTH} digits`;
        } else {
          const areaCode = rawDigits.slice(0, 3);
          const prefix = rawDigits.slice(3, 6);
          
          if (areaCode[0] === '0' || areaCode[0] === '1') {
            newErrors.phone_number = 'Area code cannot start with 0 or 1';
          } else if (prefix[0] === '0' || prefix[0] === '1') {
            newErrors.phone_number = 'Exchange code cannot start with 0 or 1';
          } else if (!isPhoneValid) {
            newErrors.phone_number = 'Please enter a valid US phone number';
          }
        }
      }

      if (formState.password !== formState.confirm_password) {
        newErrors.confirm_password = 'Passwords do not match.';
      }
  
      if (role === 'shipper') {
        if (!formState.company_name) newErrors.company_name = 'Company Name is required';
        const companyError = validateCompanyName(formState.company_name);
        if (companyError) newErrors.company_name = companyError;
        
        if (!formState.street) newErrors.street = 'Street Address is required';
        if (!formState.cityStZip) newErrors.cityStZip = 'City, State, Zip is required';
        const cityError = validateCityStateZip(formState.cityStZip);
        if (cityError) newErrors.cityStZip = cityError;
        
        if (!formState.employees) newErrors.employees = 'Number of employees is required';
      } else if (role === 'carrier') {
        const dotError = validateDOTNumber(formState.dot_number);
        if (dotError) newErrors.dot_number = dotError;
        
        if (!formState.legal_name && !formState.company_name) {
          newErrors.company_name = 'Company Name is required';
        }
        // if (!formState.fmcsa_email) newErrors.fmcsa_email = 'Business Email is required';
        
        const companyError = validateCompanyName(formState.legal_name || formState.company_name);
        if (companyError) newErrors.company_name = companyError;
        
        if (!formState.street) newErrors.street = 'Street Address is required';
        if (!formState.cityStZip) newErrors.cityStZip = 'City, State, Zip is required';
        if (formState.fmcsa_email && !validateEmail(formState.fmcsa_email)) newErrors.fmcsa_email = 'Invalid FMCSA email format';
        const cityError = validateCityStateZip(formState.cityStZip);
        if (cityError) newErrors.cityStZip = cityError;
        
        if (!formState.power_units) newErrors.power_units = 'Number of Power Units is required';
      } else if (role === 'broker') {
        const dotError = validateDOTNumber(formState.dot_number);
        if (dotError) newErrors.dot_number = dotError;
        
        if (formState.mc_number && formState.mc_number !== 'MC-' && formState.mc_number !== '') {
          const mcError = validateMCNumber(formState.mc_number);
          if (mcError) newErrors.mc_number = mcError;
        }
        
        if (!formState.legal_name && !formState.company_name) {
          newErrors.company_name = 'Company Name is required';
        }
        // if (!formState.fmcsa_email) newErrors.fmcsa_email = 'Business Email is required';

        const companyError = validateCompanyName(formState.legal_name || formState.company_name);
        if (companyError) newErrors.company_name = companyError;
        
        if (!formState.street) newErrors.street = 'Street Address is required';
        if (!formState.cityStZip) newErrors.cityStZip = 'City, State, Zip is required';
        if (formState.fmcsa_email && !validateEmail(formState.fmcsa_email)) newErrors.fmcsa_email = 'Invalid Business Email format';
        const cityError = validateCityStateZip(formState.cityStZip);
        if (cityError) newErrors.cityStZip = cityError;
      }
    }
    
    setErrors(newErrors);
    return newErrors;
  };

  const handleForceLogin = async () => {
    setSessionConflictOpen(false);
    setLoading(true);

    try {
      const res = await signinUser(role, { 
        email: formState.email, 
        password: formState.password,
        forceLogin: true
      });
      
      if (res.success && res.user) {
        processSuccessfulLogin(res);
      } else {
        throw new Error(res.message || 'Login failed.');
      }
    } catch (error) {
      setSnackbarMessage(error.message || 'An unexpected error occurred. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const processSuccessfulLogin = (res) => {
    const { user, token } = res;
    
    const hasActiveSubscription = SUBSCRIPTION_STATUSES.VALID.includes(user.subscription_status);
    const needsSubscription = SUBSCRIPTION_STATUSES.NEEDS_SUBSCRIPTION.includes(user.role);

    localStorage.setItem('jwtToken', token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('role', user.role);
    sessionStorage.setItem('session_active', 'true');

    if (needsSubscription && !hasActiveSubscription) {
      setSnackbarMessage('Sign-in successful! Redirecting to set up your subscription...');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setTimeout(() => navigate('/payment'), 1500);
      return;
    }

    setSnackbarMessage('Sign-in successful!');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
    onSignInSuccess(res.role, res.user);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!role) {
      onRoleError?.();
      return;
    }
  
    if (isRateLimited) {
      setSnackbarMessage('Too many login attempts. Please wait 5 minutes and try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      const errorKeys = Object.keys(validationErrors);
      const readable = errorKeys.map(k => FIELD_LABELS[k] || k);
      const message = `Please fix the following fields: ${readable.join(', ')}`;

      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      if (isSignUp) {
        const sanitizedData = sanitizeFormData(formState);
        
        const payload = { ...sanitizedData };
        payload.city_st_zip = sanitizedData.cityStZip;
        payload.company_name = sanitizedData.legal_name || sanitizedData.company_name;

        if (role === 'carrier' || role === 'broker') {
          payload.dot_number = sanitizedData.dot_number;
          payload.mc_number = sanitizedData.mc_number;
          payload.fmcsa_email = sanitizedData.email;
        }

        await signupUser(role, payload);
        setSnackbarMessage('Sign up successful! Please check your email for a verification link.');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        setTimeout(() => onToggle(), 2000);
      } else {
        setLoginAttempts(prev => prev + 1);
        
        const res = await signinUser(role, { 
          email: sanitizeInput(formState.email), 
          password: formState.password 
        });
        
        if (res.requiresConfirmation) {
          setSessionConflictOpen(true);
          setLoading(false);
          return;
        }
        
        if (res.success && res.user) {
          setLoginAttempts(0); 
          processSuccessfulLogin(res);
        } else {
          throw new Error(res.message || 'Login failed.');
        }
      }
    } catch (error) {
      if (!isSignUp) {
        setLoginErrorMessage(error.message || 'Invalid email or password. Please try again.');
        setLoginErrorDialogOpen(true);
      } else {
        setSnackbarMessage(error.message || 'An unexpected error occurred. Please try again.');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    } finally {
      if (isSignUp) setLoading(false);
    }
  };

  

const handleForgotPasswordSubmit = async () => {
  const sanitizedEmail = sanitizeInput(forgotPasswordEmail);

  if (!validateEmail(sanitizedEmail)) {
    setSnackbarMessage('Please enter a valid email address.');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
    return;
  }

  setForgotPasswordLoading(true);
  try {
    // Firebase sends the reset email directly — no backend call needed
    // actionCodeSettings tells Firebase where to redirect after reset
    const actionCodeSettings = {
      url: `${window.location.origin}/reset-password`,
      handleCodeInApp: false,
    };

    await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, { email: sanitizedEmail });

    setSnackbarMessage('Password reset link sent! Please check your inbox.');
    setSnackbarSeverity('success');
    setForgotPasswordOpen(false);
    setForgotPasswordEmail('');
  } catch (error) {
    // Firebase returns auth/user-not-found but we hide that for security
    if (error.code === 'auth/user-not-found') {
      // Still show success to prevent email enumeration
      setSnackbarMessage('If an account exists, a password reset link has been sent.');
      setSnackbarSeverity('success');
      setForgotPasswordOpen(false);
    } else {
      setSnackbarMessage('Failed to send reset link. Please try again.');
      setSnackbarSeverity('error');
    }
  } finally {
    setForgotPasswordLoading(false);
    setOpenSnackbar(true);
  }
};

  const getPhoneNumberHint = () => {
    return '(555) 123-4567';
  };

  const renderIdTypeFields = () => (
    <>
      <TextField
        fullWidth
        label="DOT Number"
        name="dot_number"
        value={formState.dot_number}
        onChange={handleDotChange}
        error={!!errors.dot_number}
        helperText={errors.dot_number || errors.dot_number_hint || 'Enter your DOT to fetch your details.'}
        required
        color={fieldStatus.dot_number}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        fullWidth
        label="MC Number (If Any)"
        name="mc_number"
        value={formState.mc_number}
        onChange={handleMcChange}
        error={!!errors.mc_number}
        helperText={errors.mc_number || ''}
        disabled={true}
        color={fieldStatus.mc_number}
        InputLabelProps={{ shrink: true }}
      />
    </>
  );

  const renderCompanyFields = () => (
    <>
      <TextField
        fullWidth
        label="Company Name"
        name="legal_name"
        value={formState.legal_name}
        onChange={handleChange}
        error={!!errors.legal_name || !!errors.company_name}
        helperText={errors.legal_name || errors.company_name}
        required
        disabled={true}
        color={fieldStatus.legal_name}
        InputLabelProps={{ shrink: !!formState.legal_name }}
      />
      <TextField
        fullWidth
        label="DBA Name"
        name="dba_name"
        value={formState.dba_name}
        onChange={handleChange}
        error={!!errors.dba_name}
        helperText={errors.dba_name}
        disabled={true}
        color={fieldStatus.dba_name}
        InputLabelProps={{ shrink: !!formState.dba_name }}
      />
      <TextField
        fullWidth
        label="Street Address"
        name="street"
        value={formState.street}
        onChange={handleChange}
        error={!!errors.street}
        helperText={errors.street}
        required
        disabled={true}
        color={fieldStatus.street}
        InputLabelProps={{ shrink: !!formState.street }}
      />
      <TextField
        fullWidth
        label="City, State, Zip"
        name="cityStZip"
        value={formState.cityStZip}
        onChange={handleChange}
        error={!!errors.cityStZip}
        helperText={errors.cityStZip || 'e.g., Los Angeles, CA 90001'}
        required
        disabled={true} 
        color={fieldStatus.cityStZip}
        InputLabelProps={{ shrink: !!formState.cityStZip }}
      />
    </>
  );

  return (
    <Box component="form" onSubmit={handleSubmit} className={`auth-form ${isSignUp ? 'signup' : 'signin'}`}>
      {isSignUp && (
        <>
          {role === 'broker' && (
            <>
              {renderIdTypeFields()}
              {renderCompanyFields()}
              <TextField
                disabled
                fullWidth
                label="Number of Power Units"
                name="power_units"
                type="number"
                value={0}
                helperText=""
                sx={{
                  mb: 0,
                  "& .MuiInputBase-root": {
                    backgroundColor: "#f0f0f0",
                    borderRadius: "8px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#d1d1d1 !important",
                    borderWidth: "1px",
                  },
                  "& .MuiInputLabel-root.Mui-disabled": {
                    color: "#757575",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "#9e9e9e",
                  },
                }}
              />
            </>
          )}
          
          {role === 'shipper' && (
            <>
              <TextField
                fullWidth
                label="Company Name"
                name="company_name"
                value={formState.company_name}
                onChange={handleChange}
                error={!!errors.company_name}
                helperText={errors.company_name}
                required
                color={fieldStatus.company_name}
              />
              <TextField
                fullWidth
                label="DBA Name"
                name="dba_name"
                value={formState.dba_name}
                onChange={handleChange}
                error={!!errors.dba_name}
                helperText={errors.dba_name}
                color={fieldStatus.dba_name}
              />
              <TextField
                fullWidth
                label="Street Address"
                name="street"
                value={formState.street}
                onChange={handleChange}
                error={!!errors.street}
                helperText={errors.street}
                required
                color={fieldStatus.street}
              />
              <TextField
                fullWidth
                label="City, State, Zip"
                name="cityStZip"
                value={formState.cityStZip}
                onChange={handleChange}
                error={!!errors.cityStZip}
                helperText={errors.cityStZip || 'e.g., Los Angeles, CA 90001'}
                required
                color={fieldStatus.cityStZip}
              />
              <TextField
                fullWidth
                label="Number of Employees"
                name="employees"
                type="number"
                value={formState.employees}
                onChange={handleChange}
                error={!!errors.employees}
                helperText={errors.employees}
                color={fieldStatus.employees}
              />
            </>
          )}
          
          {role === 'carrier' && (
            <>
              {renderIdTypeFields()}
              {renderCompanyFields()}
              <TextField
                fullWidth
                label="Number of Power Units"
                name="power_units"
                type="number"
                value={formState.power_units}
                onChange={handleChange}
                disabled={true}
                error={!!errors.power_units}
                helperText={errors.power_units}
                required
                color={fieldStatus.power_units}
              />
            </>
          )}
        </>
      )}

      <TextField
        fullWidth
        label="Business Email"
        name="email"
        type="email"
        value={formState.email}
        onChange={handleChange}
        onBlur={handleEmailBlur}
        error={!!errors.email}
        helperText={isCheckingEmail ? 'Checking...' : (isSignUp ? errors.email : null)}
        required
        color={fieldStatus.email}
      />

      {isSignUp && (
        <TextField
          fullWidth
          label="Phone number"
          name="phone_number"
          type="tel"
          value={formState.phone_number}
          onChange={handlePhoneNumberChange}
          error={!!errors.phone_number}
          helperText={errors.phone_number ? errors.phone_number : isPhoneValid ? '' : ''}
          required
          placeholder={getPhoneNumberHint()}
          inputProps={{ 
            maxLength: 14, 
            style: { fontSize: '16px' }
          }}
          InputProps={{
            startAdornment: (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                mr: 1,
                borderRight: '1px solid',
                borderColor: 'divider',
                pr: 1
              }}>
                <Box component="span" sx={{ 
                  color: 'text.secondary',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  fontSize: '14px'
                }}>
                  +1
                </Box>
              </Box>
            ),
          }}
          color={fieldStatus.phone_number}
        />
      )}
      
      <TextField
        fullWidth
        label="Password"
        name="password"
        type="password"
        value={formState.password}
        onChange={handleChange}
        error={!!errors.password}
        helperText={isSignUp ? errors.password || '' : null}
        required
        color={fieldStatus.password}
      />

      {isSignUp && (
        <TextField
          fullWidth
          label="Confirm Password"
          name="confirm_password"
          type="password"
          value={formState.confirm_password}
          onChange={handleChange}
          error={!!errors.confirm_password}
          helperText={errors.confirm_password}
          required
          color={fieldStatus.confirm_password}
        />
      )}

      {isSignUp && (
        <FormControlLabel
          control={
            <Checkbox
              checked={agreementChecked}
              onChange={(e) => setAgreementChecked(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" component="span">
              I agree to the{' '}
              <span
                style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={(e) => { e.preventDefault(); setShowTerms(true); }}
              >
                Terms of Service
              </span>{' '}
              and{' '}
              <span
                style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }}
              >
                Privacy Policy
              </span>
            </Typography>
          }
        />
      )}

      {!isSignUp && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, mt: -1 }}>
          <Typography
            variant="body2"
            sx={{
              color: '#1976d2',
              cursor: 'pointer',
              fontWeight: 500,
              mb: -10,
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={() => setForgotPasswordOpen(true)}
          >
            Forgot Password?
          </Typography>
        </Box>
      )}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading || (isSignUp && !agreementChecked) || isCheckingEmail || isRateLimited}
        className="form-button"
      >
        {loading ? (
          <CircularProgress size={24} color="inherit" />
        ) : isRateLimited ? (
          'Too many attempts. Please wait...'
        ) : (
          isSignUp ? 'Continue' : 'Sign In'
        )}
      </Button>

      {/* Social Login Divider */}
      <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
        <Divider sx={{ flex: 1 }} />
        <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary', fontWeight: 500 }}>
          OR
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>

      {/* Google Login Component */}
      <GoogleSignInButton 
        role={role} 
        onSignInError={(msg) => {
          setSnackbarMessage(msg);
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
        }}
        planPrice={planPrice}
        trialDays={trialDays}
        isPricingLoading={isPricingLoading}
        hideBanner={true}
      />

      {/* Pricing Banner */}
      {role && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5 },
            border: '0.5px solid',
            borderColor: 'divider',
            borderRadius: 2,
            px: 1.5,
            py: 1,
            backgroundColor: '#f8fafd',
            mt: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor:
                role === 'shipper' ? '#e3f2fd'
                  : role === 'carrier' ? '#fff8e1'
                    : '#f3e5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {role === 'shipper' && <BusinessIcon sx={{ fontSize: 18, color: '#1565c0' }} />}
            {role === 'carrier' && <LocalShippingIcon sx={{ fontSize: 18, color: '#f9a825' }} />}
            {role === 'broker' && <GroupsIcon sx={{ fontSize: 18, color: '#6a1b9a' }} />}
          </Box>

          <Box sx={{ flex: 1, minWidth: { xs: '150px', sm: 'auto' } }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.2, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              {isSignUp ? 'Your plan after trial' : 'Plan pricing'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#11224d', lineHeight: 1.4, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              {isPricingLoading
                ? 'Updating trial period...'
                : role === 'carrier'
                  ? (isSignUp ? `${trialDays || 90}-day free trial included` : `${trialDays || 90} days free trial on sign up`)
                  : (
                    <>
                      Free access
                      <br />
                      No subscription required
                    </>
                  )}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              {role === 'carrier'
                ? (!isPricingLoading && planPrice !== null
                  ? `$${Number(planPrice).toFixed(2)}/month after trial · Carrier plan`
                  : isPricingLoading ? 'Fetching price details...' : 'Pricing currently unavailable')
                : `${role ? role.charAt(0).toUpperCase() + role.slice(1) : ''} plan · No billing required`}
            </Typography>
          </Box>

          <Box
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              px: 1,
              py: 0.4,
              borderRadius: '99px',
              backgroundColor: role === 'carrier' ? '#e8f5e9' : '#e3f2fd',
              color: role === 'carrier' ? '#2e7d32' : '#1565c0',
              whiteSpace: 'nowrap',
            }}
          >
            {role === 'carrier' ? 'Free trial' : 'Free access'}
          </Box>
        </Box>
      )}

      <Typography variant="body2" align="center" className="toggle-text">
        {isSignUp ? "Already have an account?" : "Don't have an account?"}
        <Link to="#" onClick={onToggle} className="toggle-link">
          {isSignUp ? "Sign In" : "Sign Up"}
        </Link>
      </Typography>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={8000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog 
        open={sessionConflictOpen} 
        onClose={() => setSessionConflictOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1, textAlign: 'center'}}>
          Active Session Detected
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 0, fontSize: '0.95rem', textAlign: 'center' }}>
            You are already signed in on another device or browser. Do you want to sign out from that session and continue logging in here?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 1 }}>
          <Button 
            onClick={() => {
              setSessionConflictOpen(false);
              setLoading(false);
            }} 
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleForceLogin} 
            variant="contained"
            color="primary"
          >
            Yes, Sign Out Other Session
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showTerms} onClose={() => setShowTerms(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <TermsAndConditions 
            isDialog 
            onNavigate={(page) => {
              if (page === 'Privacy Policy') {
                setShowTerms(false);
                setShowPrivacy(true);
              }
            }} 
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTerms(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showPrivacy} onClose={() => setShowPrivacy(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <PrivacyPolicy isDialog />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPrivacy(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={forgotPasswordOpen} 
        onClose={() => setForgotPasswordOpen(false)} 
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>Reset Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3, fontSize: '0.9rem' }}>
            Enter the email associated with your account and we'll send a recovery link.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={forgotPasswordEmail}
            onChange={(e) => setForgotPasswordEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setForgotPasswordOpen(false)} 
            sx={{ color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleForgotPasswordSubmit} 
            variant="contained"
            disabled={forgotPasswordLoading || !forgotPasswordEmail}
          >
            {forgotPasswordLoading ? <CircularProgress size={20} /> : 'Send Link'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={loginErrorDialogOpen} 
        onClose={() => {
          setLoginErrorDialogOpen(false);
          setLoading(false);
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1, color: 'error.main', textAlign: 'center' }}>
          Sign In Failed
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, fontSize: '0.95rem', textAlign: 'center' }}>
            {loginErrorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', gap: 1 }}>
          <Button 
            onClick={() => {
              setLoginErrorDialogOpen(false);
              setForgotPasswordOpen(true);
              setLoading(false);
            }}
            variant="text"
            color="primary"
            fullWidth
            sx={{ textTransform: 'none' }}
          >
            Forgot Password?
          </Button>
          <Button 
            onClick={() => {
              setLoginErrorDialogOpen(false);
              setLoading(false);
            }}
            variant="contained"
            color="primary"
            fullWidth
          >
            Try Again
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog 
        open={dotDialog.open} 
        onClose={() => dotDialog.status === 'error' && setDotDialog({ open: false, status: 'idle' })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1, textAlign: 'center', color: dotDialog.status === 'error' ? 'error.main' : 'inherit' }}>
          {dotDialog.status === 'loading' ? 'Fetching Details' : 'Lookup Failed'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 1 }}>
          {dotDialog.status === 'loading' ? (
            <>
              <CircularProgress size={32} />
              <Typography sx={{ textAlign: 'center', fontSize: '0.95rem' }}>
                Fetching your company details from FMCSA…
              </Typography>
            </>
          ) : (
            <Typography sx={{ textAlign: 'center', fontSize: '0.95rem' }}>
              FMCSA lookup unavailable from your current location
            </Typography>
          )}
        </DialogContent>
        {dotDialog.status === 'error' && (
          <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center' }}>
            <Button 
              onClick={() => setDotDialog({ open: false, status: 'idle' })} 
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
}